import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, MessageCircle } from "lucide-react";
import Seo from "@/components/Seo";
import Results from "@/components/diagnostic/Results";
import ImpactEstimate from "@/components/diagnostic/ImpactEstimate";
import Roadmap from "@/components/diagnostic/Roadmap";
import { OptionCard, Chip, Milestones } from "@/components/diagnostic/Options";
import { prefersReducedMotion } from "@/lib/motion";
import { SITE, waLinkFor } from "@/lib/site";
import { buildBrief, saveBrief, clearBrief } from "@/lib/leadBrief";
import {
  INDUSTRIES, CHANNELS, TIME_SINKS, SYSTEMS, GOALS,
  industryById, scoreOpportunities, buildRoadmap,
} from "@/lib/discovery";

const WRAP = "mx-auto max-w-[1400px] px-5 md:px-8";

const STEPS = [
  { key: "industry", q: "What kind of business do you run?", single: true, options: INDUSTRIES, note: "Your opportunity map is taking shape." },
  { key: "channels", q: "How do customers usually reach you?", options: CHANNELS, note: "Great, we've already identified a few areas." },
  { key: "timeSinks", q: "What takes up more of your team's time than it should?", options: TIME_SINKS, chips: true, note: "Now we know where the time goes." },
  { key: "systems", q: "Where does your business information live?", options: SYSTEMS, chips: true, note: "Nearly there. We connect to what you already use." },
  { key: "goal", q: "What would you most like to improve?", single: true, options: GOALS, note: "Last one. This decides what we show first." },
];

const EMPTY = { industry: "", channels: [], timeSinks: [], systems: [], goal: "" };

export default function FindOpportunities() {
  const [params] = useSearchParams();
  const preset = INDUSTRIES.some((i) => i.id === params.get("industry")) ? params.get("industry") : "";
  const [answers, setAnswers] = useState({ ...EMPTY, industry: preset });
  const [step, setStep] = useState(preset ? 1 : 0);
  const [phase, setPhase] = useState("quiz"); // quiz | results
  const headingRef = useRef(null);
  const advanceTimer = useRef(null);

  useEffect(() => () => clearTimeout(advanceTimer.current), []);
  useEffect(() => { headingRef.current?.focus({ preventScroll: true }); }, [step, phase]);

  const live = useMemo(() => scoreOpportunities(answers), [answers]);

  /* Saved from an effect rather than from finish(): the last question advances
     on a timer, so finish() closes over the answers from before that pick.
     Here the brief always reflects what is actually on screen, and it stays
     correct if they go back and change an answer. */
  useEffect(() => {
    if (phase === "results") saveBrief(buildBrief(answers, live));
  }, [phase, answers, live]);
  const current = STEPS[step];
  const value = answers[current.key];
  const canContinue = current.single ? !!value : value.length > 0;
  const industry = industryById(answers.industry);

  const done = phase === "results" ? 5 : step >= 3 ? 2 : step >= 1 ? 1 : 0;
  const liveMilestone = phase === "results" ? -1 : step === 0 ? 0 : step < 3 ? 1 : 2;

  const finish = () => {
    setPhase("results");
    window.scrollTo({ top: 0, behavior: prefersReducedMotion() ? "auto" : "smooth" });
  };
  const advance = () => (step < STEPS.length - 1 ? setStep((s) => s + 1) : finish());

  const pickSingle = (id) => {
    setAnswers((a) => ({ ...a, [current.key]: id }));
    clearTimeout(advanceTimer.current);
    advanceTimer.current = setTimeout(advance, prefersReducedMotion() ? 0 : 320);
  };
  const toggle = (id) =>
    setAnswers((a) => {
      const list = a[current.key];
      return { ...a, [current.key]: list.includes(id) ? list.filter((x) => x !== id) : [...list, id] };
    });

  const restart = () => { clearBrief(); setAnswers(EMPTY); setStep(0); setPhase("quiz"); };

  const roadmap = useMemo(() => buildRoadmap(answers, live), [answers, live]);
  const hasRevenueOpp = live.some((o) => ["makeMore", "loseLess"].includes(o.category));
  const summary = `Hi AUT labs. ${buildBrief(answers, live).text}`;

  return (
    <div data-testid="page-find-opportunities">
      <Seo
        title="Find my opportunities · AUT labs"
        description="Five short questions. See where technology could help your business make more, lose less, work less and know more, with an illustrative impact estimate and a roadmap."
      />

      {phase === "quiz" && (
        <section className={`${WRAP} py-10 md:py-14 min-h-[60vh]`} data-testid="diagnostic">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-10 lg:gap-14 items-start">
            <div key={step} className="step-enter">
              <div className="flex items-baseline justify-between gap-4">
                <p className="eyebrow">Let's understand your business</p>
                <p className="text-[13px] text-ink/50 tabular-nums">{step + 1} of {STEPS.length}</p>
              </div>
              <h1 ref={headingRef} tabIndex={-1} className="display mt-4 text-[32px] md:text-[48px] leading-[1.02] max-w-2xl outline-none">
                {current.q}
              </h1>
              <p className="mt-3 text-[15px] text-ink/60" aria-live="polite">{current.note}</p>

              {current.single ? (
                <div role="radiogroup" aria-label={current.q} className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {current.options.map((o) => (
                    <OptionCard key={o.id} single label={o.label} sub={o.sub} active={value === o.id} onClick={() => pickSingle(o.id)} testId={`opt-${o.id}`} />
                  ))}
                </div>
              ) : current.chips ? (
                <div className="mt-8 flex flex-wrap gap-2.5">
                  {current.options.map((o) => (
                    <Chip key={o.id} label={o.label} active={value.includes(o.id)} onClick={() => toggle(o.id)} testId={`opt-${o.id}`} />
                  ))}
                </div>
              ) : (
                <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                  {current.options.map((o) => (
                    <OptionCard key={o.id} label={o.label} active={value.includes(o.id)} onClick={() => toggle(o.id)} testId={`opt-${o.id}`} />
                  ))}
                </div>
              )}

              <div className="mt-10 flex items-center gap-5">
                {step > 0 && (
                  <button type="button" onClick={() => setStep((s) => s - 1)} className="ember-link text-ink/60 text-sm inline-flex items-center gap-1.5">
                    <ArrowLeft size={14} /> Back
                  </button>
                )}
                {!current.single && (
                  <button type="button" onClick={advance} disabled={!canContinue} className="pill pill-cobalt ml-auto disabled:opacity-40 disabled:cursor-not-allowed" data-testid="continue">
                    {step === STEPS.length - 1 ? "See my opportunities" : "Continue"} <ArrowRight size={16} />
                  </button>
                )}
              </div>
            </div>

            <aside className="lg:sticky lg:top-24 border border-ink p-5 bg-paper">
              <Milestones done={done} live={liveMilestone} />
              <p className="eyebrow mt-6 mb-2">Areas identified so far</p>
              {answers.industry ? (
                <ol className="text-[14px]">
                  {live.slice(0, 4).map((o, i) => (
                    <li key={o.id} className="stagerow py-1.5 border-b border-ink/15 flex justify-between gap-3" data-on data-live={i === 0}>
                      <span>{o.problem}</span><span className="text-[11px] uppercase tracking-[0.08em] text-ink/50">{o.impact}</span>
                    </li>
                  ))}
                </ol>
              ) : (
                <p className="text-[14px] text-ink/55">Pick your business type and the first areas appear here.</p>
              )}
              <p className="text-[12px] text-ink/50 mt-4">Nothing here is sent anywhere until you choose to.</p>
            </aside>
          </div>
        </section>
      )}

      {phase === "results" && (
        <div data-testid="results-page">
          <section className="border-b border-ink">
            <div className={`${WRAP} py-10 md:py-14`}>
              <p className="eyebrow">Based on your answers · {industry.label}</p>
              <h1 ref={headingRef} tabIndex={-1} className="display mt-4 text-[34px] md:text-[56px] leading-[1] max-w-3xl outline-none">
                We found {live.length} potential {live.length === 1 ? "opportunity" : "opportunities"}.
              </h1>
              <p className="mt-4 text-ink/70 max-w-[56ch]">
                Ranked by how likely each is to matter for a business like yours. Impact is
                our read of the pattern, not a promise. We quote real numbers only after
                seeing the actual workflow.
              </p>
              <div className="mt-8"><Results opportunities={live} /></div>
            </div>
          </section>

          <section className="border-b border-ink">
            <div className={`${WRAP} section-pad`}>
              <p className="eyebrow">Estimate the impact</p>
              <h2 className="display mt-4 text-[30px] md:text-[44px] leading-[1] max-w-2xl">Is it worth doing? Put your own numbers in.</h2>
              <div className="mt-8"><ImpactEstimate hasRevenueOpp={hasRevenueOpp} /></div>
            </div>
          </section>

          <section className="border-b border-ink">
            <div className={`${WRAP} section-pad`}>
              <p className="eyebrow">Your AUT labs roadmap</p>
              <h2 className="display mt-4 text-[30px] md:text-[44px] leading-[1] max-w-2xl">Four phases. Each one useful on its own.</h2>
              <div className="mt-8"><Roadmap phases={roadmap} /></div>
            </div>
          </section>

          <section className="bg-cobalt text-paper">
            <div className={`${WRAP} section-pad grid grid-cols-1 md:grid-cols-12 gap-8 items-end`}>
              <div className="md:col-span-7">
                <h2 className="display text-[36px] md:text-[60px] leading-[0.98]">You have the map. Now get the plan.</h2>
                <p className="mt-5 text-lg text-paper/85 max-w-[46ch]">
                  Fifteen minutes on your actual workflow. We tell you which of these
                  {live.length > 1 ? ` ${live.length} ` : " "}is worth building first, what it saves,
                  and which ones to ignore.
                </p>
                <ol className="mt-8 border-t border-paper/30 max-w-[46ch]">
                  {[
                    ["You bring", "How the work moves today. No documents to prepare."],
                    ["We bring", "The full four-phase roadmap, costed and sequenced."],
                    ["You leave with", "A plan you can build with anyone. Nothing to sign."],
                  ].map(([when, what]) => (
                    <li key={when} className="flex gap-4 py-3 border-b border-paper/20">
                      <span className="eyebrow w-[104px] shrink-0" style={{ color: "inherit" }}>{when}</span>
                      <span className="text-[15px] text-paper/80">{what}</span>
                    </li>
                  ))}
                </ol>
              </div>
              <div className="md:col-span-5 flex flex-col items-start gap-4 md:border-l md:border-paper/30 md:pl-10">
                <Link to="/book" className="pill pill-paper" data-testid="results-book">Book a 15-minute call <ArrowRight size={16} /></Link>
                <p className="text-[13px] text-paper/70 max-w-[32ch]">
                  Your answers come with you. The call starts on your workflow,
                  not on background questions.
                </p>
                <a href={waLinkFor(summary)} target="_blank" rel="noreferrer" className="ember-link text-paper inline-flex items-center gap-2" data-testid="results-wa">
                  <MessageCircle size={16} /> Send this summary on WhatsApp
                </a>
                <div className="flex gap-5 text-[14px] text-paper/70">
                  <button type="button" onClick={restart} className="ember-link text-paper/80">Start over</button>
                  <a href={`mailto:${SITE.email}`} className="ember-link text-paper/80">{SITE.email}</a>
                </div>
              </div>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
